import {
  Box, Chip, IconButton, LinearProgress, ListItem, ListItemText, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import RetryIcon from '@mui/icons-material/Refresh';

import { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bytesToHumanReadableSize } from '@/utils/helpers/fileHelpers';
import useUploadStore, { IUploadFile } from '@/store/media/useUploadStore';

interface IUploadItemProps {
  upload: IUploadFile;
}

const UPLOAD_STATUS_ICON: Record<IUploadFile['status'], ReactNode> = {
  completed: <CheckIcon color="success" fontSize="small" />,
  failed: <ErrorIcon color="error" fontSize="small" />,
  cancelled: <CancelIcon color="warning" fontSize="small" />,
  uploading: <UploadIcon color="primary" fontSize="small" />,
  pending: <UploadIcon color="disabled" fontSize="small" />,
};

const UPLOAD_STATUS_COLOR = {
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
  uploading: 'primary',
  pending: 'default',
} as const;

const UPLOAD_STATUS_ACTION_ICON: Record<IUploadFile['status'], ReactNode> = {
  uploading: <CloseIcon fontSize="small" />,
  failed: <RetryIcon fontSize="small" />,
  cancelled: <RetryIcon fontSize="small" />,
  completed: <CloseIcon fontSize="small" />,
  pending: <CloseIcon fontSize="small" />,
};

const UPLOAD_STATUS_ACTION_ICON_COLOR = {
  uploading: 'warning',
  failed: 'primary',
  cancelled: 'primary',
  completed: 'error',
  pending: 'error',
} as const;

const UPLOAD_STATUS_MAIN_BACKGROUND: Record<IUploadFile['status'], string> = {
  failed: 'rgba(255, 25, 67, 0.04)',
  cancelled: 'rgba(255, 163, 25, 0.04)',
  uploading: 'transparent',
  completed: 'transparent',
  pending: 'transparent',
};

const UploadItem = ({ upload }: IUploadItemProps) => {
  const { removeUpload, cancelUpload, retryUpload } = useUploadStore();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  const UPLOAD_STATUS_ACTION_LABEL: Record<IUploadFile['status'], string> = {
    uploading: t('UPLOAD_MANAGER.STATUS_ACTION_LABEL.UPLOADING'),
    failed: t('UPLOAD_MANAGER.STATUS_ACTION_LABEL.FAILED'),
    cancelled: t('UPLOAD_MANAGER.STATUS_ACTION_LABEL.CANCELLED'),
    completed: t('UPLOAD_MANAGER.STATUS_ACTION_LABEL.COMPLETED'),
    pending: t('UPLOAD_MANAGER.STATUS_ACTION_LABEL.PENDING'),
  };

  const UPLOAD_STATUS_TEXT: Record<IUploadFile['status'], string> = {
    completed: t('UPLOAD_MANAGER.STATUS.COMPLETED'),
    failed: t('UPLOAD_MANAGER.STATUS.FAILED'),
    cancelled: t('UPLOAD_MANAGER.STATUS.CANCELLED'),
    uploading: t('UPLOAD_MANAGER.STATUS.UPLOADING'),
    pending: t('UPLOAD_MANAGER.STATUS.PENDING'),
  };

  const handleAction = () => {
    if (upload.status === 'uploading') {
      cancelUpload(upload.id);
    } else if (upload.status === 'failed' || upload.status === 'cancelled') {
      retryUpload(upload.id, queryClient);
    } else {
      removeUpload(upload.id);
    }
  };

  const roundedProgress = Math.round(upload.progress);

  return (
    <ListItem
      sx={{
        bgcolor: UPLOAD_STATUS_MAIN_BACKGROUND[upload.status],
        borderRadius: 1,
        mb: 0.5,
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {UPLOAD_STATUS_ICON[upload.status] ?? UPLOAD_STATUS_ICON.pending}
          <ListItemText
            primary={upload.file.name}
            secondary={`${bytesToHumanReadableSize(upload.file.size)} • ${upload.file.type || 'Unknown'}`}
            sx={{ mx: 1 }}
            slotProps={{
              primary: {
                variant: 'body2',
                noWrap: true,
                sx: { fontWeight: 500 },
              },
              secondary: {
                variant: 'caption',
                color: 'text.secondary',
              },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={UPLOAD_STATUS_TEXT[upload.status] ?? UPLOAD_STATUS_TEXT.pending}
              size="small"
              color={UPLOAD_STATUS_COLOR[upload.status]}
              variant={upload.status === 'uploading' ? 'filled' : 'outlined'}
            />
            <IconButton
              size="small"
              onClick={handleAction}
              color={UPLOAD_STATUS_ACTION_ICON_COLOR[upload.status]}
              title={UPLOAD_STATUS_ACTION_LABEL[upload.status]}
            >
              {UPLOAD_STATUS_ACTION_ICON[upload.status] ?? UPLOAD_STATUS_ACTION_ICON.pending}
            </IconButton>
          </Box>
        </Box>

        {/* Progress bar for active uploads */}
        {upload.status === 'uploading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={roundedProgress}
              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                minWidth: 40,
              }}
            >
              {roundedProgress}
              %
            </Typography>
          </Box>
        )}

        {/* Error message */}
        {(upload.status === 'failed' || upload.status === 'cancelled') && upload.error && (
          <Typography
            role="status"
            aria-live="polite"
            aria-atomic="true"
            variant="caption"
            color={upload.status === 'failed' ? 'error' : 'warning'}
            sx={{ mt: 0.5, display: 'block' }}
          >
            {upload.error}
          </Typography>
        )}
      </Box>
    </ListItem>
  );
};

export default UploadItem;
